/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'
import { buildHref } from '@/i18n/routing';
import Image from 'next/image'
import React, { useState, useMemo, useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import { createClient } from '@/app/[locale]/utils/supabase/client'

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  published: boolean;
  created_at: string;
  updated_at: string;
  image: string;
  seo_title: string;
  seo_desc: string;
  seo_keywords: string;
}

interface CategoryItem {
  id: string;
  name: string;
  slug: string;
  emoji?: string;
}

interface ArticlesClientProps {
  initialArticles: Article[];
  categories?: CategoryItem[];
  articleCategoriesMap?: Record<string, string[]>;
}

export default function ArticlesClient({ 
  initialArticles, 
  categories = [],
  articleCategoriesMap = {}
}: ArticlesClientProps) {
  const t = useTranslations('articles')
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const router = useRouter();
  const searchParams = useSearchParams();
  const currentLocale = useLocale();

  // State to store articles merged with translations
  const [articlesData, setArticlesData] = useState<Article[]>(initialArticles);
  
  // Store a map of category IDs to their data for quick lookups
  const categoryMap = useMemo(() => {
    const map: Record<string, CategoryItem> = {};
    categories.forEach(cat => {
      map[cat.id] = cat;
    });
    return map;
  }, [categories]);
  
  // Create a map of slugs to category IDs for easy lookup
  const categorySlugMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach(cat => {
      if (cat.slug) {
        map[cat.slug] = cat.id;
      }
    });
    return map;
  }, [categories]);

  useEffect(() => {
    // Look for category param - could be slug or ID
    const categoryParam = searchParams.get('category') || '';
    
    // Determine if it's a slug or ID and set the selected category ID
    if (categoryParam) {
      // If it directly matches an ID, use it
      if (categoryMap[categoryParam]) {
        setSelectedCategoryId(categoryParam);
      } 
      // If it matches a slug, convert to ID
      else if (categorySlugMap[categoryParam]) {
        setSelectedCategoryId(categorySlugMap[categoryParam]);
      }
      // Otherwise clear selection
      else {
        setSelectedCategoryId('');
      }
    } else {
      setSelectedCategoryId('');
    }
  }, [searchParams, categoryMap, categorySlugMap]);

  // Available categories for filtering
  const availableCategories = useMemo(() => {
    if (categories && categories.length > 0) {
      return categories.map(cat => cat.id);
    }
    
    // Fallback to extracting unique categories from the articleCategoriesMap
    const uniqueCategories = new Set<string>();
    Object.values(articleCategoriesMap).forEach(categoryIds => {
      categoryIds.forEach(catId => uniqueCategories.add(catId));
    });
    return Array.from(uniqueCategories);
  }, [categories, articleCategoriesMap]);

  // Filter articles based on search query and selected category
  const filteredArticles = articlesData.filter(article => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Get category IDs for this article from the junction table map
    const articleCategoryIds = articleCategoriesMap[article.id] || [];
    
    const matchesCategory = selectedCategoryId
      ? articleCategoryIds.includes(selectedCategoryId)
      : true;
    
    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filteredArticles.length / postsPerPage);

  // Compute visible articles using pagination
  const visibleArticles = useMemo(() => {
    const startIndex = (currentPage - 1) * postsPerPage;
    return filteredArticles.slice(startIndex, startIndex + postsPerPage);
  }, [filteredArticles, currentPage]);

  // Fetch translations from Supabase if current locale is not English
  useEffect(() => {
    async function fetchTranslations() {
      const supabase = createClient();
      const articleIds = initialArticles.map(article => article.id);
      const { data, error } = await supabase
        .from('article_translations')
        .select('article_id, title, slug, excerpt')
        .in('article_id', articleIds)
        .eq('locale', currentLocale);
      if (error) {
        return;
      }

      // Build a mapping from article_id to translated title and slug
      const translationMap = new Map<string, { title: string; slug: string; excerpt: string }>();
      data.forEach((item: any) => {
        translationMap.set(item.article_id, { 
          title: item.title, 
          slug: item.slug,
          excerpt: item.excerpt
        });
      });

      const updatedArticles = initialArticles.map(article => {
        const translation = translationMap.get(article.id);
        if (translation) {
          return { 
            ...article, 
            title: translation.title || article.title, 
            slug: translation.slug || article.slug,
            excerpt: translation.excerpt || article.excerpt
          };
        }
        return article;
      });
      setArticlesData(updatedArticles);
    }

    if (currentLocale && currentLocale !== 'en') {
      fetchTranslations();
    } else {
      setArticlesData(initialArticles);
    }
  }, [initialArticles, currentLocale]);

  const handleCategoryClick = (catId: string) => {
    // Get the category from the map
    const category = categoryMap[catId];
    if (!category) return;
    
    const newCategoryId = selectedCategoryId === catId ? '' : catId;
    
    // Use slug for URL if available, otherwise use ID
    const categoryParam = newCategoryId ? (category.slug || catId) : '';
    const query = categoryParam ? `?category=${encodeURIComponent(categoryParam)}` : '';
    
    router.push(query);
    setSelectedCategoryId(newCategoryId);
    setCurrentPage(1);
  };

  // Helper to generate a slug fallback from the title
  const generateSlug = (text: string): string => {
    return text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
  };

  // Format date - always using English formatting
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="container max-w-7xl px-8 mx-auto py-12 bg-bg-main text-text-main">
      <div className="h-[92px] w-full"/>
      {/* Header */}
      <header className="mb-12 text-left">
        <h1 className="text-2xl md:text-4xl text-text-main font-bold">
          {t('hero.title')}
        </h1>
        <p className="mt-4 text-base md:text-xl text-text-main">
          {t('hero.subtitle')}
        </p>
      </header>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-8">
        <div className="flex flex-wrap gap-4 font-semibold">
          <button
            onClick={() => { router.push(''); setSelectedCategoryId(''); setCurrentPage(1); }}
            className={`px-4 py-1 rounded-full transition ${
              selectedCategoryId === "" ? "bg-secondary text-static-white" : "bg-primary text-text-main hover:bg-secondary hover:text-static-white"
            }`}
          >
            {t('filters.allTags')}
          </button>
          {availableCategories.map((catId) => {
            const category = categoryMap[catId];
            return (
              <button
                key={catId}
                onClick={() => handleCategoryClick(catId)}
                className={`px-4 py-1 rounded-full transition ${
                  selectedCategoryId === catId ? "bg-secondary text-static-white" : "bg-primary text-text-main hover:bg-secondary hover:text-static-white"
                }`}
              >
                {category?.emoji && <span className="mr-1">{category.emoji}</span>}
                {category?.name || catId}
              </button>
            );
          })}
        </div>
        <div className="w-full md:w-1/3">
          <input
            type="text"
            placeholder={t('filters.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full p-3 rounded-2xl bg-primary bg-opacity-10 placeholder:text-text-main text-text-main focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Blog Posts Grid/List */}
      {visibleArticles.length === 0 ? (
        <div className="text-center text-text-main py-8">{t('noArticles')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visibleArticles.map((article) => {
            // Get the categories for this article from the junction table
            const articleCategoryIds = articleCategoriesMap[article.id] || [];
            
            return (
              <div
                key={article.id}
                className="overflow-hidden transition rounded-xl border border-border hover:border-primary bg-surface/50"
              >
                <div className="relative h-48 w-full">
                  <button
                    onClick={() =>
                      router.push(
                        `/${currentLocale}${buildHref('/articles/[slug]', { slug: article.slug || generateSlug(article.title) })}`
                      )
                    }
                    className="w-full h-full"
                  >
                    {article.image ? (
                      <Image
                        draggable={false} onContextMenu={(e) => e.preventDefault()} 
                        src={article.image}
                        alt={article.title}
                        fill
                        className="object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-primary-gradient-from to-primary-gradient-to" />
                    )}
                  </button>
                </div>
                <div className='py-4 px-4 bg-surface'>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {articleCategoryIds.map((catId) => {
                      const category = categoryMap[catId];
                      if (!category) return null;
                      
                      // Use the category slug for the URL
                      const categoryParam = category.slug || catId;
                      
                      return (
                        <button
                          key={catId}
                          onClick={(e) => {
                            e.preventDefault();
                            router.push(`?category=${encodeURIComponent(categoryParam)}`);
                            setSelectedCategoryId(catId);
                            setCurrentPage(1);
                          }}
                          className="bg-primary/20 text-primary font-semibold text-sm px-2 py-1 rounded-xl"
                        >
                          {category.emoji && <span className="mr-1">{category.emoji}</span>}
                          {category.name}
                        </button>
                      );
                    })}
                    {articleCategoryIds.length > 0 && (
                      <span className="text-text-secondary mx-2 text-md">
                        {' • '}
                      </span>
                    )}
                    <span className="text-text-secondary text-md">
                      {formatDate(article.created_at)}
                    </span>
                  </div>
                  <h2 className="text-xl text-text-main font-bold mb-2">
                    <button
                      onClick={() =>
                        router.push(
                          `/${currentLocale}${buildHref('/articles/[slug]', { slug: article.slug || generateSlug(article.title) })}`
                        )
                      }
                      className="hover:text-primary transition-colors text-2xl"
                    >
                      {article.title}
                    </button>
                  </h2>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8 space-x-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-md bg-primary text-text-main disabled:opacity-50"
          >
            {t('pagination.previous')}
          </button>
          <span className="px-4 py-2 text-text-main">
            {t('pagination.pageInfo', { current: currentPage, total: totalPages })}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-md bg-secondary text-text-main disabled:opacity-50"
          >
            {t('pagination.next')}
          </button>
        </div>
      )}
    </div>
  );
}
